/** @internal – import from `@/store/useDepartmentStore` instead. */
import type { GeoPipelineInput } from "../types";
import { runSSEStream } from "../sseRunner";
import { buildErrorMessage } from "../utils";
import { sseEvents } from "../../../events";

/**
 * GEO research SSE pipeline runner. Posts to
 * `/core/coagent/geo-research` and publishes typed `geo:*` events. The
 * subscriber (`wireGeoSubscriber`) translates events into store
 * mutations.
 *
 * Pipeline-internal state:
 *   - `resolvedGeoInput` is updated when the server returns
 *     `targetQueries` (during citation_baseline running/complete and
 *     done) so the final `done` event carries the right input shape
 *     even if the user never provided queries.
 *
 * Throttling state (`citationStartLogged`,
 * `lastCitationProgressLogged`, `citationWarnedQueries`) was moved to
 * the subscriber — pipeline only emits raw events.
 *
 * Migrated off the `SSECallbacks` mutator-bag in Phase 8 commit 73.
 */
export async function runGeoResearchSSE(
  geoInput: GeoPipelineInput,
  sessionId: string,
  signal?: AbortSignal,
): Promise<void> {
  const normalizeGeoQueries = (raw: unknown): string[] | null => {
    if (!Array.isArray(raw)) return null;
    const seen = new Set<string>();
    const queries: string[] = [];
    for (const value of raw) {
      if (typeof value !== "string") continue;
      const query = value.trim();
      if (!query) continue;
      const key = query.toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      queries.push(query);
      if (queries.length >= 10) break;
    }
    return queries.length > 0 ? queries : null;
  };

  await runSSEStream(geoInput, {
    pipeline: "geo-research",
    endpoint: "/core/coagent/geo-research",
    sessionId,
    buildBody: (sid, input) => ({ sessionId: sid, geoInput: input }),
    errorPrefix: "GEO research",
    signal,
    debugMeta: { businessName: geoInput.businessName, hasAbortSignal: Boolean(signal) },
    handleEvent: (event) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { phase, status, data, error, details } = event as Record<string, any>;
      const ev = data as Record<string, unknown> | undefined;

      if (phase === "session_named" && ev?.name) {
        sseEvents.publish({
          type: "geo:session-named",
          pipeline: "geo-research",
          sessionId,
          name: ev.name as string,
        });
        return;
      }

      if (phase === "site_scan" && status === "running") {
        sseEvents.publish({
          type: "geo:site-scan-running",
          pipeline: "geo-research",
          sessionId,
          url: typeof ev?.url === "string" ? ev.url : "",
        });
        return;
      }

      if (phase === "site_scan" && status === "complete") {
        sseEvents.publish({
          type: "geo:site-scan-complete",
          pipeline: "geo-research",
          sessionId,
          data: ev ?? {},
        });
        return;
      }

      if (phase === "citation_baseline" && status === "running") {
        sseEvents.publish({
          type: "geo:citation-baseline-running",
          pipeline: "geo-research",
          sessionId,
          data: ev ?? {},
          progress: {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            completed: Number((event as any)?.progress?.completed ?? 0),
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            total: Number((event as any)?.progress?.total ?? 0),
          },
          resolvedTargetQueries: normalizeGeoQueries(ev?.targetQueries),
        });
        return;
      }

      if (phase === "citation_baseline" && status === "complete") {
        sseEvents.publish({
          type: "geo:citation-baseline-complete",
          pipeline: "geo-research",
          sessionId,
          data: ev ?? {},
          resolvedTargetQueries: normalizeGeoQueries(ev?.targetQueries),
        });
        return;
      }

      if (phase === "gap_analysis" && status === "running") {
        sseEvents.publish({
          type: "geo:gap-analysis-running",
          pipeline: "geo-research",
          sessionId,
        });
        return;
      }

      if (phase === "gap_analysis" && status === "complete") {
        sseEvents.publish({
          type: "geo:gap-analysis-complete",
          pipeline: "geo-research",
          sessionId,
          data: ev ?? {},
        });
        return;
      }

      if (phase === "asset_generation" && status === "running") {
        sseEvents.publish({
          type: "geo:asset-generation-running",
          pipeline: "geo-research",
          sessionId,
        });
        return;
      }

      if (phase === "asset_generation" && status === "complete") {
        sseEvents.publish({
          type: "geo:asset-generation-complete",
          pipeline: "geo-research",
          sessionId,
          data: ev ?? {},
        });
        return;
      }

      if (phase === "entity_authority" && status === "running") {
        sseEvents.publish({
          type: "geo:entity-authority-running",
          pipeline: "geo-research",
          sessionId,
        });
        return;
      }

      if (phase === "entity_authority" && status === "complete") {
        sseEvents.publish({
          type: "geo:entity-authority-complete",
          pipeline: "geo-research",
          sessionId,
          data: ev ?? {},
        });
        return;
      }

      if (phase === "implementation_plan" && status === "running") {
        sseEvents.publish({
          type: "geo:implementation-plan-running",
          pipeline: "geo-research",
          sessionId,
        });
        return;
      }

      if (phase === "implementation_plan" && status === "complete") {
        sseEvents.publish({
          type: "geo:implementation-plan-complete",
          pipeline: "geo-research",
          sessionId,
          data: ev ?? {},
        });
        return;
      }

      if (phase === "done") {
        sseEvents.publish({
          type: "geo:done",
          pipeline: "geo-research",
          sessionId,
          data: ev ?? {},
          resolvedTargetQueries: normalizeGeoQueries(
            ev?.targetQueries ?? ev?.target_queries,
          ),
        });
        return;
      }

      if (phase === "error") {
        const message = buildErrorMessage(error, details, "GEO research pipeline failed");
        console.error("[coagent/geo-research] SSE error", { sessionId, message, details });
        sseEvents.publish({
          type: "geo:error",
          pipeline: "geo-research",
          sessionId,
          message,
          ...(details !== undefined ? { details } : {}),
        });
      }
    },
  });
}
